/* eslint-disable */

import type React from "react";
import Link from "next/link";
import { Options as ReactRendererOptions } from "@contentful/rich-text-react-renderer";
import { INLINES } from "@contentful/rich-text-types";

// Make URL safe for href="..." since output is raw HTML.
export const escapeAttr = (value: string): string => value.replace(/&/g, "&amp;").replace(/"/g, "&quot;");

// Resolves the href for a linked entry (e.g. PageGeneral) using the
// `links.entries.hyperlink` array returned alongside a rich text field's json.
// entry-hyperlink is restricted (via migration) to internal content types
// only; plain external URLs go through the "hyperlink" node type instead.
export const buildEntryHrefMap = (entryLinks?: any[]): Record<string, string> => {
  const map: Record<string, string> = {};
  entryLinks?.forEach((entry: any) => {
    if (!entry?.sys?.id) return;
    if (entry.__typename === "PageGeneral" && entry.slug) {
      map[entry.sys.id] = `/${entry.slug}`;
    }
  });
  return map;
};
export const resolveHref = (uri: string, locale?: string): string => {
  const isAbsolute = /^https?:\/\//i.test(uri);
  if (isAbsolute) return uri;
  if (locale && uri.startsWith("/") && uri !== `/${locale}` && !uri.startsWith(`/${locale}/`)) {
    return `/${locale}${uri}`;
  }
  return uri;
};

// `entryLinks` should be the `links.entries.hyperlink` array returned by GraphQL
// for the specific rich text field being rendered, so entry-hyperlink nodes
// (links to other Contentful entries, e.g. PageGeneral) resolve correctly.
export const getRichTextOptions = (entryLinks?: any[], locale?: string) => {
  const entryHrefMap = buildEntryHrefMap(entryLinks);
  return {
    renderNode: {
      [INLINES.HYPERLINK]: (node: any, next: (content: any) => string) => {
        const uri: string = node?.data?.uri || "";
        const linkText = next(node.content);
        const href = resolveHref(uri, locale);
        const target = /^https?:\/\//i.test(uri) ? ' target="_blank" rel="noopener noreferrer"' : "";
        return `<a href="${escapeAttr(href)}"${target}>${linkText}</a>`;
      },
      [INLINES.ENTRY_HYPERLINK]: (node: any, next: (content: any) => string) => {
        const linkText = next(node.content);
        const entryId = node?.data?.target?.sys?.id;
        const uri = entryId ? entryHrefMap[entryId] : undefined;
        // Without a resolvable href (e.g. the query didn't fetch `links` for
        // this field, or the linked content type isn't handled above), fall
        // back to plain text instead of the renderer's default debug span.
        if (!uri) return linkText;
        return `<a href="${escapeAttr(resolveHref(uri, locale))}">${linkText}</a>`;
      }
    }
  };
};

// Next's <Link> only works as a real React element in the render tree (it
// needs to hydrate and attach its own client-side navigation handler), so it
// can't be used inside an HTML string built for dangerouslySetInnerHTML.
// documentToReactComponents renders an actual React tree instead, letting us
// use <Link> for internal entry-hyperlinks so the Next router handles them.
// Reusable for any rich text field rendered with documentToReactComponents
// (pass that field's own `links.entries.hyperlink` array as entryLinks).
export const getRichTextReactOptions = (entryLinks?: any[], locale?: string): ReactRendererOptions => {
  const entryHrefMap = buildEntryHrefMap(entryLinks);
  const renderLink = (uri: string, children: React.ReactNode) => {
    const href = resolveHref(uri, locale);
    const isAbsolute = /^https?:\/\//i.test(uri);
    if (isAbsolute) {
      return <a href={href} target="_blank" rel="noopener noreferrer">
          {children}
        </a>;
    }
    return <Link href={href}>{children}</Link>;
  };
  return {
    renderNode: {
      [INLINES.HYPERLINK]: (node: any, children: React.ReactNode) => {
        const uri: string = node?.data?.uri || "";
        return renderLink(uri, children);
      },
      [INLINES.ENTRY_HYPERLINK]: (node: any, children: React.ReactNode) => {
        const entryId = node?.data?.target?.sys?.id;
        const uri = entryId ? entryHrefMap[entryId] : undefined;
        if (!uri) return <>{children}</>;
        return renderLink(uri, children);
      }
    }
  };
};